import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Box, Button } from '@bosch/react-frok'

import ChatFeatures from '@/ui/components/ChatFeatures/ChatFeatures'
import CustomMarkdown from '@/ui/components/CustomMarkdown/CustomMarkdown'
import AttachmentGallery from '@/ui/components/AttachmentGallery/AttachmentGallery'
import ChatImage from '@/ui/components/ChatImage/ChatImage'
import MessageSources from '@/ui/components/ChatMessageTypes/MessageSources/MessageSources'
import FollowUpMessage from '@/ui/components/FollowUpMessage/FollowUpMessage'
import MessageWrapper from '@/ui/components/MessageWrapper/MessageWrapper'
import GroupMessage from '@/ui/components/GroupMessage/GroupMessage'
import ErrorRecoveryMessage from './ErrorRecoveryMessage'
import ChatStatusMessage from '@/ui/components/ChatStatusMessage/ChatStatusMessage'
import useAssistantIllustration from '@/hooks/useAssistantIllustration'
import type { InternalMessage } from '@/utils/ai-adapters/xdc/types'
import type { AssistantMessageProps } from './types/AssistantMessageTypes'
import type { ChatStatusMessageItem } from '../../ChatStatusMessage/types/ChatStatusMessageTypes'
import type { RootState } from '@/utils/store/store'
import { deriveStatusItems, injectContent } from '../../ChatStatusMessage/deriveStatusItems'
import { buildThinkingStatusItems } from '../../ChatStatusMessage/statusItemBuilders'
import useBrowsePhaseProgress from '../../ChatStatusMessage/useBrowsePhaseProgress'
import { useAgentLoadingState } from '../../ChatStatusMessage/useAgentLoadingState'

import 'streamdown/styles.css'
import './AssistantMessage.scss'

const AssistantMessageComponent = ({
    activeAssistantConfig,
    message,
    messageList = [],
    messageIndex = 0,
    lastAssistantMessageIndex,
    features,
    isLastMessage: isLastMessageProp,
    isDarkTheme,
    onRetry,
    isGlobalSearchClicked,
    onGlobalSearchClick,
    isWelcomeMessage,
    waitingForResponse,
}: AssistantMessageProps) => {
    const assistantImageAllowInversion = activeAssistantConfig?.appearance?.imageAllowInversion || false
    const isTroubleshootingAssistant = activeAssistantConfig?.endpoint === 'issue-resolution'
    const isIpnSearchEnabled = activeAssistantConfig?.features?.advanced?.ipnSearch
    const outputFeatures = activeAssistantConfig?.features?.output
    const { t } = useTranslation()

    const { image: assistantImage, backgroundColor: assistantImageBackgroundColor } = useAssistantIllustration()
    const { chatWaitsForResponse } = useSelector((state: RootState) => state.assistants)

    const [showCarouselImages, setShowCarouselImages] = useState(false)
    const [persistedStatusItems, setPersistedStatusItems] = useState<ChatStatusMessageItem[]>([])
    const [wasGlobalSearch, setWasGlobalSearch] = useState(false)
    const [followedByGlobalSearch, setFollowedByGlobalSearch] = useState(false)

    const isHandoverMsg = !!message.assistantHandover
    const prevMsg = messageList[messageIndex - 1]

    const isSameRequest: boolean = !message.userMessageId
        ? true
        : !prevMsg?.userMessageId
            ? false
            : prevMsg.userMessageId === message.userMessageId

    const isPrevHandover = !!prevMsg?.assistantHandover && isSameRequest
    const isGroupMember = isHandoverMsg && isPrevHandover
    const isFirstInSequence = isHandoverMsg && !isPrevHandover

    const groupMessages = useMemo<InternalMessage[]>(() => {
        if (!isFirstInSequence || messageList.length === 0) return []
        const group: InternalMessage[] = []
        let j = messageIndex
        const groupUserMsgId = messageList[j]?.userMessageId
        while (j < messageList.length && messageList[j]?.assistantHandover) {
            const m = messageList[j]
            if (j > messageIndex && m.userMessageId && m.userMessageId !== groupUserMsgId) break
            group.push(m)
            j++
        }
        return group.length >= 2 ? group : []
    }, [isFirstInSequence, messageList, messageIndex])

    const isGroupStart = groupMessages.length >= 2

    const isLastMessage = lastAssistantMessageIndex !== undefined
        ? isGroupStart
            ? (messageIndex + groupMessages.length - 1) === lastAssistantMessageIndex
            : messageIndex === lastAssistantMessageIndex
        : isLastMessageProp

    const isCurrentStreamFinished = isGroupStart
        ? groupMessages[groupMessages.length - 1]?.finishedMessage === true
        : message.finishedMessage === true

    const { state: loadingState, groupSize } = useAgentLoadingState(
        activeAssistantConfig,
        messageList,
        !!waitingForResponse
    )
    const { browsePhaseVisible, browsingChildVisible, browsingDoneVisible } = useBrowsePhaseProgress(loadingState)

    // Only latch for the global-search group itself (stream not yet finished when clicked).
    // Local search stream is already done at the moment of click --> guard prevents it latching.
    useEffect(() => {
        if (isLastMessage && isGlobalSearchClicked && !wasGlobalSearch && !isCurrentStreamFinished) {
            setWasGlobalSearch(true)
        }
    }, [isLastMessage, isGlobalSearchClicked, wasGlobalSearch, isCurrentStreamFinished])

    const isGlobalPath = wasGlobalSearch

    // Latches when this message stops being the last one while global search is active.
    // Drives the connector line from the local search result down to the global search group.
    // Set during the render (not in an effect): the line to the follow-up hands over to this line in the same render,
    // so it stays in place instead of disappearing for a frame and growing again from the top.
    const [prevIsLastMessage, setPrevIsLastMessage] = useState(isLastMessage)
    if (prevIsLastMessage !== isLastMessage) {
        setPrevIsLastMessage(isLastMessage)
        if (prevIsLastMessage && !isLastMessage && isGlobalSearchClicked) {
            setFollowedByGlobalSearch(true)
        }
    }

    const dynamicStatusItems = useMemo(
        () => deriveStatusItems(t, {
            loadingState, messageList, groupSize, isGlobalPath,
            browsePhaseVisible, browsingChildVisible, browsingDoneVisible,
        }),
        [loadingState, groupSize, isGlobalPath, messageList, browsePhaseVisible, browsingChildVisible, browsingDoneVisible, t]
    )

    useEffect(() => {
        if (isLastMessage && (loadingState === 'optimistic_thinking' || loadingState === 'agent_thinking')) {
            setPersistedStatusItems([])
        }
    }, [isLastMessage, loadingState])

    useEffect(() => {
        if (isLastMessage && loadingState === 'responding') {
            const lastMsg = messageList[messageList.length - 1]
            if (lastMsg?.finishedMessage) {
                const doneItems = dynamicStatusItems.filter((item) => item.state === 'done' || !item.state)
                if (doneItems.length > 0) setPersistedStatusItems(doneItems)
            }
        }
    }, [isLastMessage, loadingState, dynamicStatusItems, messageList])

    const displayStatusItems = isLastMessage ? dynamicStatusItems : persistedStatusItems

    // Show connector from this (local-search) group down to the global-search group below.
    const connectToNext = (isTroubleshootingAssistant && followedByGlobalSearch) && !isLastMessage && displayStatusItems.length > 0

    // A failed answer is replaced by the recovery message (retry, or report the problem to the developers)
    const isRetryable = message.errorRecovery === 'retryable'
    const isUnrecoverable = message.errorRecovery === 'unrecoverable'
    if ((isRetryable || isUnrecoverable) && isLastMessage) {
        return (
            <ErrorRecoveryMessage
                assistantImage={assistantImage}
                assistantImageAllowInversion={assistantImageAllowInversion}
                isDarkTheme={isDarkTheme}
                isRetryable={isRetryable}
                errorPayload={message.errorPayload}
                onRetry={onRetry}
            />
        )
    }

    if (isGroupMember) return null

    if (isFirstInSequence && !isGroupStart && chatWaitsForResponse && isLastMessage) {
        const earlyContent = message.content || message.message || undefined
        const earlyHasContent = !!earlyContent && earlyContent.length > 0
        if (!earlyHasContent) {
            return dynamicStatusItems.length > 0 ? (
                <div className="assistant-message">
                    <ChatStatusMessage items={dynamicStatusItems} />
                </div>
            ) : null
        }
    }

    if (isGroupStart) {
        const lastGroupMsg = groupMessages[groupMessages.length - 1]
        const isStreamFinished = lastGroupMsg.finishedMessage === true
        const groupContent = (
            <GroupMessage
                type="solution"
                messages={groupMessages}
                features={messageIndex !== 0 ? features : undefined}
                outputFeatures={outputFeatures}
                isDarkTheme={isDarkTheme}
                isLastMessage={isLastMessage}
                isStreaming={chatWaitsForResponse && !isStreamFinished && isLastMessage}
            />
        )
        const hasFollowUp = isTroubleshootingAssistant && isLastMessage && isStreamFinished && !isGlobalPath && !!lastGroupMsg.messageId && isIpnSearchEnabled
        const items = displayStatusItems.length > 0 ? injectContent(displayStatusItems, groupContent) : []

        return (
            <div className="assistant-message group-message">
                {items.length > 0 ? <ChatStatusMessage items={items} connectToFollowUp={hasFollowUp || connectToNext} /> : groupContent}
                {hasFollowUp && (
                    <FollowUpMessage
                        isDarkTheme={isDarkTheme}
                        isGlobalSearchTriggered={isGlobalSearchClicked}
                        onGlobalSearchClick={onGlobalSearchClick}
                        messageId={lastGroupMsg.messageId}
                    />
                )}
            </div>
        )
    }

    const content = message.content || message.message || ''
    const hasContent = content && content.length !== 0
    const isStreamFinished = message.finishedMessage === true

    if (!hasContent && chatWaitsForResponse && !isLastMessage) return null

    // Handover single messages show the status panel while waiting for content.
    if (!hasContent && chatWaitsForResponse && isLastMessage && isHandoverMsg) {
        return dynamicStatusItems.length > 0 ? (
            <div className="assistant-message">
                <ChatStatusMessage items={dynamicStatusItems} />
            </div>
        ) : null
    }

    // Troubleshooting assistant with no content yet: use ChatStatusMessage so shimmer,
    // animations, and isTroubleshootingQuery gating are all handled consistently.
    if (!hasContent && chatWaitsForResponse && isLastMessage && isTroubleshootingAssistant) {
        return dynamicStatusItems.length > 0 ? (
            <div className="assistant-message single-message">
                <ChatStatusMessage items={dynamicStatusItems} />
            </div>
        ) : null
    }

    // Other assistants: the same "Thinking..." status until the first content arrives
    if (!hasContent && chatWaitsForResponse && isLastMessage) {
        return (
            <div className="assistant-message single-message">
                <ChatStatusMessage items={buildThinkingStatusItems(t)} />
            </div>
        )
    }

    const isStreaming = chatWaitsForResponse && !isStreamFinished && isLastMessage

    // Split the message content at the image block so AttachmentGallery is rendered
    // inline at exactly the position images appear — preserving document order.
    //
    // Two patterns recognised in one pass:
    //   [![alt](thumb)](url) — linked image: thumb in gallery, url opens on click
    //   ![alt](url)          — standalone image: same url for thumb and full
    // Plus h2 heading links (Streamdown renders <a> inside <h2> as a button,
    // so we extract them before Streamdown sees them):
    //   ## [text](url)
    const ANY_IMAGE_RE = /(?:\[!\[([^\]]*)\]\(([^\s]+)\)\]\(([^\s]+)\)|!\[([^\]]*)\]\(([^\s]+)\))/g
    const H2_LINK_RE   = /^##\s+\[([^\]]*)\]\(([^\s]+)\)\s*$/gm
    const contentStr   = hasContent ? (content as string) : ''
    const allImageMatches = hasContent ? [...contentStr.matchAll(ANY_IMAGE_RE)] : []
    const allH2Matches    = hasContent ? [...contentStr.matchAll(H2_LINK_RE)]   : []

    const markdownGalleryItems = [
        ...allImageMatches.map(m =>
            m[1] !== undefined
                ? { thumb: m[2], full: m[3] }   // [![alt](thumb)](url)
                : { thumb: m[5], full: m[5] }   // ![alt](url)
        ),
        ...allH2Matches.map(m => ({ thumb: m[2], full: m[2] })),
    ]

    // Combine all match positions (sorted) to find the first and last image in the content
    const allMatchPositions = [
        ...allImageMatches.map(m => ({ index: m.index!, end: m.index! + m[0].length })),
        ...allH2Matches.map(m => ({ index: m.index!, end: m.index! + m[0].length })),
    ].sort((a, b) => a.index - b.index)

    let contentBefore = contentStr
    let contentAfter  = ''
    if (allMatchPositions.length > 0) {
        const firstIdx = allMatchPositions[0].index
        const lastEnd  = allMatchPositions[allMatchPositions.length - 1].end
        contentBefore  = contentStr.slice(0, firstIdx).replace(/\n{3,}/g, '\n\n').trimEnd()
        contentAfter   = contentStr.slice(lastEnd).replace(/\n{3,}/g, '\n\n').trimStart()
    }

    const messageContent = (
        <div className="message-wrapper">
            <ChatImage
                image={assistantImage}
                backgroundColor={assistantImageBackgroundColor}
                imageAllowInversion={assistantImageAllowInversion}
                isDarkTheme={isDarkTheme}
                rounded
                visibility={isHandoverMsg ? "hidden" : "visible"}
            />
            <MessageWrapper
                content={content ?? ''}
                hasContent={true}
                outputFeatures={outputFeatures}
                messageType="assistant"
                isWelcomeMessage={isWelcomeMessage}
                isStreaming={chatWaitsForResponse && !isStreamFinished && isLastMessage}
            >
                <Box className="message-box message-box-chatbot">
                    <div className="message-content">
                        {hasContent && (
                            <CustomMarkdown isStreaming={isStreaming}>
                                {(contentBefore || contentStr)}
                            </CustomMarkdown>
                        )}
                        {markdownGalleryItems.length > 0 && (
                            <>
                                <AttachmentGallery items={markdownGalleryItems} />
                                {contentAfter && (
                                    <CustomMarkdown isStreaming={isStreaming}>
                                        {(contentAfter)}
                                    </CustomMarkdown>
                                )}
                            </>
                        )}
                        <MessageSources message={message} />
                        {message.imageCarousel && (
                            <>
                                <Button
                                    label={message.imageCarousel.showImagesLabel}
                                    title={message.imageCarousel.showImagesLabel}
                                    mode={showCarouselImages ? 'secondary' : 'primary'}
                                    icon={{ iconName: 'image-gallery', title: message.imageCarousel.showImagesLabel }}
                                    onClick={() => setShowCarouselImages(v => !v)}
                                />
                                {showCarouselImages && (
                                    <AttachmentGallery
                                        items={message.imageCarousel.images.map(url => ({ thumb: url, full: url }))}
                                    />
                                )}
                            </>
                        )}
                    </div>
                </Box>
                <ChatFeatures features={features} message={message} />
            </MessageWrapper>
        </div>
    )

    const hasFollowUp = isTroubleshootingAssistant && isLastMessage && isStreamFinished && !isGlobalPath && isHandoverMsg && !!message.messageId && isIpnSearchEnabled
    const items = isHandoverMsg && displayStatusItems.length > 0 ? injectContent(displayStatusItems, messageContent) : []

    return (
        <div className="assistant-message single-message">
            {items.length > 0 ? <ChatStatusMessage items={items} connectToFollowUp={hasFollowUp || connectToNext} /> : messageContent}
            {hasFollowUp && (
                <FollowUpMessage
                    isDarkTheme={isDarkTheme}
                    isGlobalSearchTriggered={isGlobalSearchClicked}
                    onGlobalSearchClick={onGlobalSearchClick}
                    messageId={message.messageId}
                />
            )}
        </div>
    )
}

const AssistantMessage = React.memo(AssistantMessageComponent)

export default AssistantMessage
